import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { QualityBadge } from "./QualityBadge";
import { Listing, MATERIAL_LABELS } from "@/types";
import { MapPin, Package, DollarSign, ArrowUpDown, ImageIcon, Heart, GitCompare, Share2 } from "lucide-react";
import { useSavedListings, useToggleSavedListing } from "@/hooks/useSavedListings";
import { useCompare } from "@/hooks/useCompareStore";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { ListingPhoto } from "@/components/listings/ListingPhoto";

export function ListingCard({ listing, distanceMiles }: { listing: Listing; distanceMiles?: number }) {
  const hasPhotos = listing.photos && listing.photos.length > 0;
  const { user } = useAuth();
  const { data: saved = [] } = useSavedListings();
  const isSaved = saved.some((s) => s.listing_id === listing.id);
  const toggleSaved = useToggleSavedListing();
  const compare = useCompare();
  const inCompare = compare.has(listing.id);

  const stop = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onSave = (e: React.MouseEvent) => {
    stop(e);
    if (!user) {
      toast.error("Sign in to save listings");
      return;
    }
    toggleSaved.mutate({ listingId: listing.id, saved: isSaved });
  };

  const onCompare = (e: React.MouseEvent) => {
    stop(e);
    compare.toggle(listing.id);
    toast.success(inCompare ? "Removed from compare" : "Added to compare");
  };

  const onShare = async (e: React.MouseEvent) => {
    stop(e);
    const url = `${window.location.origin}/marketplace/${listing.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: listing.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <Link to={`/marketplace/${listing.id}`}>
      <Card className="group relative h-full overflow-hidden transition-all hover:shadow-lg hover:border-accent/40 hover:-translate-y-0.5">
        {hasPhotos ? (
          <div className="aspect-[16/9] overflow-hidden bg-muted">
            <ListingPhoto
              value={listing.photos[0]}
              alt={listing.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              fallback={
                <div className="flex h-full w-full items-center justify-center">
                  <ImageIcon className="h-10 w-10 text-muted-foreground/30" />
                </div>
              }
            />
          </div>
        ) : (
          <div className="aspect-[16/9] flex items-center justify-center bg-muted">
            <ImageIcon className="h-10 w-10 text-muted-foreground/30" />
          </div>
        )}

        {/* Hover quick actions */}
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            onClick={onSave}
            aria-label={isSaved ? "Remove from saved" : "Save listing"}
            className={`flex h-8 w-8 items-center justify-center rounded-full bg-background/90 backdrop-blur shadow hover:bg-background ${
              isSaved ? "text-rose-500" : "text-foreground"
            }`}
          >
            <Heart className={`h-4 w-4 ${isSaved ? "fill-rose-500" : ""}`} />
          </button>
          <button
            type="button"
            onClick={onCompare}
            aria-label={inCompare ? "Remove from compare" : "Add to compare"}
            className={`flex h-8 w-8 items-center justify-center rounded-full bg-background/90 backdrop-blur shadow hover:bg-background ${
              inCompare ? "text-accent" : "text-foreground"
            }`}
          >
            <GitCompare className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onShare}
            aria-label="Share listing"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-background/90 backdrop-blur shadow hover:bg-background"
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>

        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <Badge variant={listing.type === "source" ? "default" : "secondary"} className="mb-2 text-xs">
                {listing.type === "source" ? "Source Material" : "Receiving Site"}
              </Badge>
              <h3 className="font-display font-semibold text-base leading-tight line-clamp-2">
                {listing.title}
              </h3>
            </div>
            <QualityBadge tier={listing.qualityTier} />
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Package className="h-4 w-4 shrink-0" />
            <span>{MATERIAL_LABELS[listing.materialType]}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <ArrowUpDown className="h-4 w-4 shrink-0" />
            <span>{listing.quantity.toLocaleString()} {listing.unit === "tons" ? "Tons" : "CY"}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4 shrink-0" />
            <span>
              {listing.location.city}, {listing.location.state}
              {distanceMiles != null && (
                <span className="ml-1 text-xs text-accent">· {distanceMiles.toFixed(1)} mi</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <DollarSign className="h-4 w-4 shrink-0" />
            <span className="font-semibold text-foreground">
              {listing.pricePerUnit === 0 ? "Free" : `$${listing.pricePerUnit}/${listing.unit === "tons" ? "ton" : "CY"}`}
            </span>
          </div>
          <p className="pt-1 text-xs text-muted-foreground">
            Posted {new Date(listing.postedDate).toLocaleDateString()}
          </p>
        </CardContent>
      </Card>
    </Link>
  );
}
